import { useState, useEffect, useRef, useCallback } from 'react';
import {
  getSellers,
  createSeller,
  updateSeller,
  deleteSeller,
  Seller,
} from '@/services/sellersService';
import { uploadFile } from '@/lib/uploadFile';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  Plus,
  Trash2,
  Edit,
  Users,
  Link2,
  UploadCloud,
  X,
  Loader2,
} from 'lucide-react';

const EMPTY_FORM = { name: '', link: '', image: null as string | null, hide: false };

const SellersList = () => {
  const [sellers, setSellers] = useState<Seller[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Seller | null>(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  // Form state
  const [form, setForm] = useState(EMPTY_FORM);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const fetchSellers = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getSellers();
      setSellers(data);
    } catch (error) {
      console.error('Error fetching sellers:', error);
      toast.error('Failed to load sellers. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSellers();
  }, [fetchSellers]);

  const openAddDialog = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setImageFile(null);
    setImagePreview(null);
    setDialogOpen(true);
  };

  const openEditDialog = (seller: Seller) => {
    setEditing(seller);
    setForm({
      name: seller.name,
      link: seller.link,
      image: seller.image,
      hide: seller.hide,
    });
    setImageFile(null);
    setImagePreview(seller.image);
    setDialogOpen(true);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }
    if (!form.link.trim()) {
      toast.error('Link is required');
      return;
    }

    try {
      setSaving(true);

      let imageUrl = form.image;
      if (imageFile) {
        setUploading(true);
        imageUrl = await uploadFile(imageFile);
      }

      const payload = {
        name: form.name.trim(),
        link: form.link.trim(),
        image: imageUrl,
        hide: form.hide,
      };

      if (editing) {
        await updateSeller(editing.id, payload);
        toast.success('Seller updated');
      } else {
        await createSeller(payload);
        toast.success('Seller created');
      }

      setDialogOpen(false);
      fetchSellers();
    } catch (error) {
      console.error('Error saving seller:', error);
      const message =
        (error as { message?: string })?.message || 'Failed to save seller. Please try again.';
      toast.error(message);
    } finally {
      setUploading(false);
      setSaving(false);
    }
  };

  const handleToggleHide = async (seller: Seller, hide: boolean) => {
    try {
      const updated = await updateSeller(seller.id, { hide });
      setSellers((prev) => prev.map((s) => (s.id === seller.id ? updated : s)));
      toast.success(hide ? 'Seller hidden from front page' : 'Seller visible on front page');
    } catch (error) {
      console.error('Error toggling seller visibility:', error);
      toast.error('Failed to update seller visibility. Please try again.');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      setDeletingId(id);
      await deleteSeller(id);
      setSellers((prev) => prev.filter((s) => s.id !== id));
      toast.success('Seller deleted');
    } catch (error) {
      console.error('Error deleting seller:', error);
      toast.error('Failed to delete seller. Please try again.');
    } finally {
      setDeletingId(null);
    }
  };

  const getInitials = (name: string) =>
    name
      .split(' ')
      .map((part) => part.charAt(0))
      .join('')
      .slice(0, 2)
      .toUpperCase();

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Sellers</h1>
          <p className="text-muted-foreground">Manage sellers shown on the front page</p>
        </div>
        <Button onClick={openAddDialog}>
          <Plus className="mr-2 h-4 w-4" />
          Add Seller
        </Button>
      </div>

      <Card className="bg-background border border-border/50 shadow-subtle">
        <CardHeader className="pb-2">
          <CardTitle>Sellers</CardTitle>
          <CardDescription>
            {loading ? 'Loading...' : `${sellers.length} seller(s)`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="flex flex-col items-center gap-2">
                <div className="w-10 h-10 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
                <span className="text-sm text-muted-foreground">Loading sellers...</span>
              </div>
            </div>
          ) : sellers.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 gap-4">
              <div className="w-16 h-16 rounded-full bg-muted/30 flex items-center justify-center">
                <Users className="h-8 w-8 text-muted-foreground" />
              </div>
              <div className="text-center">
                <h3 className="text-lg font-medium">No sellers yet</h3>
                <p className="text-muted-foreground">Add your first seller</p>
              </div>
              <Button variant="outline" onClick={openAddDialog}>
                <Plus className="mr-2 h-4 w-4" />
                Add Seller
              </Button>
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Profile</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Link</TableHead>
                    <TableHead>Visible</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sellers.map((seller) => (
                    <TableRow key={seller.id}>
                      <TableCell>
                        <Avatar className="h-10 w-10">
                          {seller.image ? (
                            <AvatarImage src={seller.image} alt={seller.name} />
                          ) : null}
                          <AvatarFallback>{getInitials(seller.name)}</AvatarFallback>
                        </Avatar>
                      </TableCell>
                      <TableCell>
                        <p className="font-medium">{seller.name}</p>
                        {seller.hide && (
                          <Badge variant="secondary" className="mt-1">
                            Hidden
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {seller.link ? (
                          <a
                            href={seller.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-primary hover:underline text-sm"
                          >
                            <Link2 className="h-3.5 w-3.5" />
                            <span className="max-w-[220px] truncate">{seller.link}</span>
                          </a>
                        ) : (
                          <span className="text-muted-foreground text-sm">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={!seller.hide}
                            onCheckedChange={(checked) => handleToggleHide(seller, !checked)}
                          />
                          <span className="text-xs text-muted-foreground">
                            {seller.hide ? 'Hidden' : 'Visible'}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => openEditDialog(seller)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-destructive"
                                disabled={deletingId === seller.id}
                              >
                                {deletingId === seller.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Trash2 className="h-4 w-4" />
                                )}
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent className="bg-background border border-border/50">
                              <AlertDialogHeader>
                                <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This will permanently delete the seller "{seller.name}". This action
                                  cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel className="bg-background hover:bg-muted">
                                  Cancel
                                </AlertDialogCancel>
                                <AlertDialogAction
                                  className="bg-destructive text-white hover:bg-destructive/90"
                                  onClick={() => handleDelete(seller.id)}
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add / Edit seller dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-background border border-border/50">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Seller' : 'Add Seller'}</DialogTitle>
            <DialogDescription>
              {editing
                ? 'Update the seller details below.'
                : 'Add a new seller to show on the front page.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="seller-name">Name</Label>
              <Input
                id="seller-name"
                value={form.name}
                onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="Seller name"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="seller-link">Link</Label>
              <Input
                id="seller-link"
                type="url"
                value={form.link}
                onChange={(e) => setForm((prev) => ({ ...prev, link: e.target.value }))}
                placeholder="https://example.com"
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Profile picture</Label>
              <div className="flex items-center justify-center w-full">
                <Label
                  htmlFor="seller-image"
                  className="flex flex-col items-center justify-center w-full h-36 border-2 border-dashed rounded-lg cursor-pointer bg-background/50 hover:bg-background/80 border-border/50 transition-colors"
                >
                  <div className="flex flex-col items-center justify-center py-4">
                    <UploadCloud className="w-8 h-8 mb-2 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">
                      <span className="font-semibold">Click to upload</span> or drag and drop
                    </p>
                    <p className="text-xs text-muted-foreground">PNG, JPG or GIF</p>
                  </div>
                  <Input
                    id="seller-image"
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    accept="image/*"
                    onChange={handleImageChange}
                  />
                </Label>
              </div>
              {imagePreview && (
                <div className="relative inline-block mt-2">
                  <Avatar className="h-20 w-20">
                    <AvatarImage src={imagePreview} alt="Profile" />
                    <AvatarFallback>{getInitials(form.name || '?')}</AvatarFallback>
                  </Avatar>
                  <button
                    type="button"
                    onClick={removeImage}
                    className="absolute -top-1 -right-1 bg-destructive text-white rounded-full p-1"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between rounded-md border p-3">
              <div className="space-y-0.5">
                <Label htmlFor="seller-hide">Hidden from front page</Label>
                <p className="text-xs text-muted-foreground">
                  Hidden sellers will not appear on the public site.
                </p>
              </div>
              <Switch
                id="seller-hide"
                checked={form.hide}
                onCheckedChange={(checked) => setForm((prev) => ({ ...prev, hide: checked }))}
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={saving || uploading}>
                {saving || uploading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {uploading ? 'Uploading...' : 'Saving...'}
                  </>
                ) : editing ? (
                  'Save changes'
                ) : (
                  'Add Seller'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SellersList;
